import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useCms } from '../../contexts/CmsContext';
import { Zap, Users, Award, Phone, User, Mail, ArrowRight, Loader2, Check, Shield, Gift, MessageCircle } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import { googleLogin, captureLead } from '../../api/client';
import type { User as UserType } from '../../types/auth';

const iconMap: Record<string, React.ElementType> = { Zap, Users, Award };
const defaultFeatures = [
  { icon: 'Zap', text: 'AI-powered learning paths' },
  { icon: 'Users', text: 'Learn with industry experts' },
  { icon: 'Award', text: 'Get certified & hired' },
];

export default function LoginScreen(): React.JSX.Element {
  const cms = useCms();
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, setAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const features = cms.getJson('login_features_json', defaultFeatures);
  const from = (location.state as any)?.from || localStorage.getItem('redirect_after_login') || '/dashboard';

  // --- Lead capture state for bouncers ---
  const [leadForm, setLeadForm] = useState({ name: '', phone: '' });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadError, setLeadError] = useState('');
  const [leadSuccess, setLeadSuccess] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      localStorage.removeItem('redirect_after_login');
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      setLoading(true);
      const idToken = credentialResponse.credential;
      if (!idToken) {
        toast.show('Google login failed: no token received', 'error');
        return;
      }

      const { getReferralCode } = await import('../../utils/utm');
      const referralCode = getReferralCode();
      const response = await googleLogin(idToken, '', 'web', undefined, referralCode || undefined);
      console.log('[Login] API response:', response);

      if (response.error) {
        toast.show(response.error || 'Login failed', 'error');
        return;
      }

      const { access_token, refresh_token, user } = response;
      if (access_token && user) {
        setAuth(access_token, refresh_token, user as UserType);
        toast.show('Welcome back, ' + user.name + '!', 'success');
      } else {
        console.error('[Login] Missing access_token or user in response:', { access_token, user, response });
        toast.show('Invalid login response from server', 'error');
      }
    } catch (error: any) {
      toast.show(error.message || 'Google sign-in failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    toast.show('Google sign-in failed. Please try again.', 'error');
    setLoading(false);
  };

  const handleLeadSubmit = async () => {
    if (!leadForm.name.trim() || !leadForm.phone.trim()) {
      setLeadError('Please fill in all fields');
      return;
    }
    setLeadSubmitting(true);
    setLeadError('');
    try {
      const res = await captureLead({
        name: leadForm.name.trim(),
        email: '',
        phone: leadForm.phone.trim(),
        source: 'login_page',
        campaign: 'free_resources_whatsapp',
      });
      if (res.success) {
        setLeadSuccess(true);
      } else {
        setLeadError(res.message || 'Something went wrong. Please try again.');
      }
    } catch (err: any) {
      setLeadError(err?.message || 'Network error. Please try again.');
    } finally {
      setLeadSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 gradient-mesh opacity-30" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/ecoleminds.png" alt="EcoleMinds" className="h-10 w-auto object-contain" />
          </div>

          <div className="space-y-8">
            <h2 className="text-4xl font-bold leading-tight">
              {cms.get('login_hero_title', 'Join 2,000+ Finance Professionals')}<br />
              <span className="text-amber-400">{cms.get('login_hero_highlight', 'Learning AI the Practical Way')}</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-md">
              {cms.get('login_hero_subtitle', 'CAs, Bankers, FP&A & Risk professionals mastering AI — without heavy coding. RBI-compliant. India-focused. Job-ready.')}
            </p>
            <div className="space-y-4">
              {features.map((f: any, i: number) => {
                const Icon = iconMap[f.icon] || Zap;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-amber-400" />
                    </div>
                    <span className="text-slate-300 font-medium">{f.text}</span>
                  </motion.div>
                );
              })}
            </div>

            {/* Social proof micro-bar */}
            <div className="flex items-center gap-4 pt-2">
              <div className="flex items-center gap-1.5 text-sm text-slate-300">
                <span className="text-amber-400">★★★★★</span>
                <span className="font-semibold">4.9/5</span>
              </div>
              <div className="w-px h-4 bg-slate-700" />
              <span className="text-sm text-slate-400">500+ Reviews</span>
              <div className="w-px h-4 bg-slate-700" />
              <span className="text-sm text-slate-400">Trusted by HDFC, ICICI, Axis</span>
            </div>
          </div>

          <p className="text-slate-500 text-sm">
            {cms.get('trusted_by_text', 'Trusted by 10,000+ students and 500+ companies worldwide')}
          </p>
        </div>
      </div>

      {/* Right side - Login */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50/50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <img src="/ecoleminds.png" alt="EcoleMinds" className="h-10 w-auto object-contain" />
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-xl shadow-slate-200/50 border border-slate-100">
            <div className="text-center mb-8">
              <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-amber-500 transition-colors mb-4">
                <ArrowRight className="w-4 h-4 rotate-180" /> Back to Home
              </Link>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">{cms.get('login_welcome_title', 'Welcome back')}</h1>
              <p className="text-slate-500 text-sm">
                {cms.get('login_welcome_subtitle', 'Sign in to continue your learning journey')}
              </p>
            </div>

            <div className={`flex justify-center ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                size="large"
                width={320}
                text="continue_with"
                theme="outline"
                shape="rectangular"
              />
            </div>

            <div className="mt-6 text-center">
              <p className="text-xs text-slate-400">
                By signing in, you agree to our{' '}
                <a href="#" className="text-amber-500 hover:underline">Terms</a>
                {' '}and{' '}
                <a href="#" className="text-amber-500 hover:underline">Privacy Policy</a>
              </p>
            </div>
          </div>

          {/* Lead capture card for bouncers */}
          <div className="mt-5 bg-white rounded-3xl p-6 shadow-xl shadow-slate-200/50 border border-slate-100">
            {leadSuccess ? (
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-1">Check Your WhatsApp!</h3>
                <p className="text-sm text-slate-500">Your free AI Finance Starter Kit is on its way.</p>
              </div>
            ) : (
              <>
                <div className="text-center mb-4">
                  <h3 className="text-base font-bold text-slate-900">Not Ready to Enroll?</h3>
                  <p className="text-sm text-slate-500">Get our Free AI Finance Starter Kit on WhatsApp. No commitment.</p>
                </div>

                <div className="space-y-3">
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                      <User className="w-4 h-4" />
                    </div>
                    <input
                      type="text"
                      placeholder="Your name"
                      value={leadForm.name}
                      onChange={(e) => {
                        setLeadForm({ ...leadForm, name: e.target.value });
                        setLeadError('');
                      }}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-11 pr-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all"
                    />
                  </div>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                      <Phone className="w-4 h-4" />
                    </div>
                    <input
                      type="tel"
                      placeholder="WhatsApp number"
                      value={leadForm.phone}
                      onChange={(e) => {
                        setLeadForm({ ...leadForm, phone: e.target.value });
                        setLeadError('');
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && handleLeadSubmit()}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-11 pr-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all"
                    />
                  </div>
                  {leadError && <p className="text-xs text-red-500">{leadError}</p>}
                  <button
                    onClick={handleLeadSubmit}
                    disabled={leadSubmitting}
                    className="w-full py-2.5 bg-green-500 hover:bg-green-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold text-sm rounded-xl transition-colors shadow-lg shadow-green-500/25 flex items-center justify-center gap-2"
                  >
                    {leadSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <MessageCircle className="w-4 h-4" />
                        Send to My WhatsApp
                      </>
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-center gap-3 pt-3 mt-3 border-t border-slate-100">
                  <div className="flex items-center gap-1 text-[10px] text-slate-400">
                    <Shield className="w-3 h-3" />
                    100% Secure
                  </div>
                  <div className="flex items-center gap-1 text-[10px] text-slate-400">
                    <Gift className="w-3 h-3" />
                    Instant Delivery
                  </div>
                  <div className="flex items-center gap-1 text-[10px] text-slate-400">
                    <Check className="w-3 h-3" />
                    No Spam
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="mt-6 text-center lg:hidden">
            <p className="text-sm text-slate-500">
              Learn AI skills from industry experts. Enroll in courses, attend workshops, and get certified.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
